import { Link } from 'react-router';
import { Alert } from '../../ui/feedback';
import { Card } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';
import type { UnlinkedRun } from './types';

/**
 * JIRA-20: a deploy run for the attempt's revision finished, but Jira could not link it (usually a deploy workflow
 * without the contract's run-name), so the attempt waits for its executor's report or an admin's reconciliation.
 */
export function UnlinkedRunAlert({
  attempt,
  executor,
  runs,
  link,
}: {
  attempt: number;
  executor: string | null;
  runs: UnlinkedRun[];
  link?: string;
}) {
  const run = runs[0]!;
  return (
    <Alert tone="warning" title={`A deploy run finished but is not linked to ATT-${attempt}`}>
      <span>
        <a href={run.url} target="_blank" rel="noreferrer">
          {run.title || 'Deploy run'}
        </a>{' '}
        ({run.conclusion}) ran this attempt&apos;s revision, but Jira could not link it: {run.reason}{' '}
        {executor ? `${executor}, the executor,` : 'The executor'} reports the outcome, or an admin reconciles
        ATT-{attempt}. Fix the deploy workflow so the next runs link by themselves.
        {link && (
          <>
            {' '}
            <Link to={link}>Open ATT-{attempt}</Link>
          </>
        )}
      </span>
    </Alert>
  );
}

/** Every finished deploy run for this release Jira could not link, with the reason. */
export function UnlinkedRunsCard({ runs }: { runs: UnlinkedRun[] }) {
  if (!runs.length) return null;
  return (
    <Card
      title="Unlinked deploy runs"
      subtitle="Runs of the deploy workflow Jira received but could not link."
    >
      <ul className="checklist">
        {runs.map((r) => (
          <li key={r.url} className="stack sm" style={{ alignItems: 'flex-start' }}>
            <span>
              <a href={r.url} target="_blank" rel="noreferrer">
                {r.title || 'Deploy run'}
              </a>{' '}
              <span className="small muted">
                {r.conclusion} · <Timestamp value={r.finishedAt ?? r.receivedAt} />
                {r.revision ? ` · ${r.revision.slice(0, 10)}` : ''}
              </span>
            </span>
            <span className="small muted">{r.reason}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
